import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Phone, PhoneOff, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function IncomingCallOverlay({ incomingCall, onAccept, onReject }) {
  const { t } = useTranslation();
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setPulse(p => !p), 1000);
    return () => clearInterval(interval);
  }, []);

  if (!incomingCall) return null;

  const { callId, callType, from } = incomingCall;
  const isVideo = callType === 'video';
  const name = `${from.firstName || ''} ${from.lastName || ''}`.trim() || 'Unknown';
  const initials = `${from.firstName?.[0] || ''}${from.lastName?.[0] || ''}`.toUpperCase();

  const overlay = (
    <div className="incoming-call-overlay" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        background: 'var(--c-surface)', borderRadius: 20, padding: '32px 40px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
        boxShadow: '0 8px 40px rgba(0,0,0,0.3)', minWidth: 280, maxWidth: 340,
        border: '1px solid var(--c-border)',
      }}>
        {/* Avatar with pulse */}
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute', inset: -8, borderRadius: '50%',
            border: `2px solid ${isVideo ? '#3b82f6' : '#22c55e'}`,
            opacity: pulse ? 0.6 : 0, transform: pulse ? 'scale(1.2)' : 'scale(1)',
            transition: 'all 0.5s ease',
          }} />
          {from.avatarUrl ? (
            <img src={from.avatarUrl} alt={name} style={{
              width: 72, height: 72, borderRadius: '50%', objectFit: 'cover',
            }} />
          ) : (
            <div style={{
              width: 72, height: 72, borderRadius: '50%', background: 'var(--c-brand)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, fontWeight: 700,
            }}>
              {initials}
            </div>
          )}
        </div>

        {/* Name */}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--c-text)', marginBottom: 4 }}>
            {name}
          </div>
          <div style={{ fontSize: 14, color: 'var(--c-text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {isVideo ? <Video size={16} /> : <Phone size={16} />}
            {isVideo ? t('chat.incomingVideoCall') : t('chat.incomingCall')}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 24, marginTop: 8 }}>
          <button onClick={() => onReject(callId)} style={{
            width: 56, height: 56, borderRadius: '50%', border: 'none',
            background: '#ef4444', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(239,68,68,0.4)',
            transition: 'transform 0.15s',
          }}
            className="call-btn-reject"
          >
            <PhoneOff size={24} />
          </button>

          <button onClick={() => onAccept(callId)} style={{
            width: 56, height: 56, borderRadius: '50%', border: 'none',
            background: '#22c55e', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(34,197,94,0.4)',
            animation: 'call-btn-ring 1.5s ease-in-out infinite',
          }}
            className="call-btn-accept"
          >
            {isVideo ? <Video size={24} /> : <Phone size={24} />}
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
}
